import { useState } from 'react';
import { useAtomValue } from 'jotai';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { applyLanguage } from '@/lib/i18n';
import { useLanguages } from '@/hooks/useLanguages';
import { currentLangAtom } from '@/store/langAtom';

export default function LanguageSwitcher() {
  const activeLang = useAtomValue(currentLangAtom);
  const { languages, isLoading } = useLanguages();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isActive = (code: string) => activeLang === code;

  const current = languages.find(l => isActive(l.appLanguageCode));
  const currentLabel = current?.description ?? activeLang;
  const currentCode = current?.appLanguageCode ?? activeLang;

  if (isLoading) {
    return <Skeleton variant="rounded" width="100%" height={34} />;
  }

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Language: ${currentLabel}`}
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<LanguageIcon sx={{ fontSize: 16 }} />}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              transition: 'transform 150ms',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        }
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          textTransform: 'none',
          fontSize: '0.75rem',
          fontWeight: 500,
          color: 'text.secondary',
          borderColor: 'divider',
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          '&:hover': {
            borderColor: 'action.disabled',
            bgcolor: 'action.hover',
          },
        }}
      >
        <span dir="auto" style={{ flex: 1, textAlign: 'start' }}>
          {currentLabel}
        </span>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: { sx: { borderRadius: 3, minWidth: 160, mt: 0.5 } },
        }}
      >
        {languages.map(({ appLanguageCode, description }) => {
          const active = isActive(appLanguageCode);
          return (
            <MenuItem
              key={appLanguageCode}
              selected={active}
              onClick={() => { applyLanguage(appLanguageCode); setAnchorEl(null); }}
              sx={{ fontSize: '0.8125rem' }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {active && <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
              </ListItemIcon>
              <ListItemText>
                <Box
                  component="span"
                  dir="auto"
                  sx={{
                    fontWeight: active ? 600 : 400,
                    color: active ? 'primary.main' : undefined,
                  }}
                >
                  {description}
                </Box>
              </ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
